/**
 * Video Facade Component
 *
 * Implements a "click-to-load" pattern for video iframes to improve performance.
 * Shows a lightweight thumbnail image initially, only loads the iframe when user clicks.
 * This significantly reduces initial page weight and improves INP (Interaction to Next Paint).
 *
 * Uses YouTube Player API for reliable autoplay on mobile browsers.
 * @param {HTMLElement} container - The video facade container element
 * @param {Promise} consentPromise - Promise that resolves with consent data
 * @return {Component}
 */

import Component from "../../lib/Component.js";
import { withNoAdsFailover } from "../helpers.js";

const FAST_THUMBNAIL_LOAD_THRESHOLD_MS = 800;

const VideoFacade = (container, consentPromise) => {
  if (!container || !container.classList.contains("video-facade")) {
    return Component.noop();
  }

  const platform = container.dataset.videoPlatform;
  const videoId = container.dataset.videoId;
  const title = container.dataset.videoTitle;
  const playButton = container.querySelector(".video-facade-play-button");
  const thumbnail = container.querySelector(".video-facade-thumbnail");
  let videoLoaded = false;
  let cleanupAutoLoadBenchmark = () => {};

  const now = () => {
    return window.performance && typeof window.performance.now === "function" ? window.performance.now() : Date.now();
  };

  const isUnavailableYouTubeThumbnail = () => {
    return (
      platform === "youtube" &&
      thumbnail &&
      thumbnail.naturalWidth === 120 &&
      thumbnail.naturalHeight === 90 &&
      thumbnail.src.split("/").pop() !== "default.jpg"
    );
  };

  const hasUsableThumbnail = () => {
    return thumbnail && thumbnail.complete && thumbnail.naturalWidth > 0 && !isUnavailableYouTubeThumbnail();
  };

  const setupYouTubeThumbnailFallback = () => {
    // YouTube thumbnail quality levels in descending order
    const thumbnailQualities = [
      "maxresdefault.jpg", // 1920x1080 - not always available
      "sddefault.jpg", // 640x480
      "hqdefault.jpg", // 480x360
      "mqdefault.jpg", // 320x180
      "default.jpg", // 120x90 - always available
    ];

    // Detect current quality from the src attribute
    const currentSrc = thumbnail.src;
    let currentQualityIndex = 0;

    for (let i = 0; i < thumbnailQualities.length; i++) {
      if (currentSrc.includes(thumbnailQualities[i])) {
        currentQualityIndex = i;
        break;
      }
    }

    const tryNextQuality = () => {
      currentQualityIndex++;
      if (currentQualityIndex < thumbnailQualities.length) {
        const nextQuality = thumbnailQualities[currentQualityIndex];
        thumbnail.src = `https://i.ytimg.com/vi/${videoId}/${nextQuality}`;
      }
    };

    const tryHigherQuality = () => {
      // Only try higher quality if we're not already at the highest
      if (currentQualityIndex === 0) return;

      // Check if element size justifies higher quality
      const renderedWidth = thumbnail.offsetWidth;
      const renderedHeight = thumbnail.offsetHeight;

      // If element is larger than current quality, try loading maxresdefault
      // sddefault is 640x480, so upgrade if rendered size is significantly larger
      if (renderedWidth > 640 || renderedHeight > 480) {
        const higherQualityUrl = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

        // Load in background
        const testImage = new Image();
        testImage.onload = () => {
          // Check if it's not a placeholder (maxresdefault has 1920x1080, or at least > 640x480)
          if (testImage.naturalWidth > 640 && testImage.naturalHeight > 480) {
            // Successfully loaded higher quality, replace
            thumbnail.src = higherQualityUrl;
            currentQualityIndex = 0;
          }
        };
        // Silently fail if maxresdefault doesn't exist
        testImage.onerror = () => {};
        testImage.src = higherQualityUrl;
      }
    };

    const checkAndFallback = () => {
      // YouTube returns a 120x90 placeholder image for unavailable thumbnails
      // Check if the loaded image is the placeholder (very small dimensions)
      if (isUnavailableYouTubeThumbnail()) {
        tryNextQuality();
        return false;
      }
      return true;
    };

    // Check on load if we got the placeholder
    thumbnail.addEventListener("load", checkAndFallback);
    // Also handle actual errors
    thumbnail.addEventListener("error", tryNextQuality);

    // After 1 second, check if the image failed to load and fallback
    setTimeout(() => {
      if (checkAndFallback()) {
        tryHigherQuality();
      }
    }, 1000);
  };

  const loadVideo = async (autoplay = false) => {
    if (videoLoaded) return;

    videoLoaded = true;
    cleanupAutoLoadBenchmark();

    if (playButton && thumbnail) {
      playButton.removeEventListener("click", clickHandler);
      thumbnail.removeEventListener("click", clickHandler);
    }

    // Remove the facade elements
    container.classList.remove("video-facade");
    container.innerHTML = "";

    if (platform === "youtube") {
      await loadYouTubeVideo(autoplay);
    } else if (platform === "vimeo") {
      loadVimeoVideo(autoplay);
    }
  };

  const loadYouTubeVideo = async (autoplay = false) => {
    // Check analytics consent to determine which YouTube domain to use
    const { analytics, reason } = await withNoAdsFailover(consentPromise, { analytics: false });
    if (process.env.NODE_ENV === "development")
      console.log("[YouTube Video Loader] Analytics consent:", analytics, "Reason:", reason);
    const youtubeHost = analytics ? "https://www.youtube.com" : "https://www.youtube-nocookie.com";

    // Create a div for YouTube Player API (it needs a div, not iframe)
    const playerDiv = document.createElement("div");
    playerDiv.id = `yt-player-${videoId}-${Date.now()}`;
    container.appendChild(playerDiv);

    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    const initPlayer = () => {
      const _player = new window.YT.Player(playerDiv.id, {
        height: "100%",
        width: "100%",
        videoId: videoId,
        host: youtubeHost,
        playerVars: {
          playsinline: 1,
          hl: "hu",
        },
        events: {
          onReady: (event) => {
            // Attempt to play - this works on mobile because it's triggered by user click
            if (autoplay) {
              // Mute on iOS WebKit to ensure autoplay works
              if (
                /iPhone|iPad|iPod/.test(navigator.userAgent) &&
                /WebKit/.test(navigator.userAgent) &&
                !/CriOS/.test(navigator.userAgent)
              ) {
                event.target.mute();
              }
              event.target.playVideo();
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }
  };

  const loadVimeoVideo = (autoplay = false) => {
    // For Vimeo, create iframe with autoplay
    const iframe = document.createElement("iframe");
    iframe.width = "100%";
    iframe.height = "100%";
    iframe.title = title;
    iframe.frameBorder = "0";
    iframe.allow =
      "accelerometer; autoplay; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    iframe.allowFullscreen = true;
    iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=${autoplay ? "1" : "0"}&autopause=0`;

    container.appendChild(iframe);
  };

  const setupAutoLoadBenchmark = () => {
    if (!thumbnail) return () => {};

    const benchmarkStartedAt = now();

    const loadIfFast = () => {
      const loadDuration = now() - benchmarkStartedAt;

      if (loadDuration <= FAST_THUMBNAIL_LOAD_THRESHOLD_MS && hasUsableThumbnail()) {
        loadVideo(false);
      }
    };

    if (hasUsableThumbnail()) {
      loadIfFast();
      return () => {};
    }

    thumbnail.addEventListener("load", loadIfFast);

    const timeoutId = setTimeout(() => {
      thumbnail.removeEventListener("load", loadIfFast);
    }, FAST_THUMBNAIL_LOAD_THRESHOLD_MS);

    return () => {
      clearTimeout(timeoutId);
      thumbnail.removeEventListener("load", loadIfFast);
    };
  };

  // Setup YouTube thumbnail fallback
  if (platform === "youtube" && thumbnail) {
    setupYouTubeThumbnailFallback();
  }

  // Store click handler for cleanup
  const clickHandler = () => loadVideo(true);

  return new Component({
    componentName: "VideoFacade",
    onMount() {
      if (playButton) {
        playButton.addEventListener("click", clickHandler);
        thumbnail.addEventListener("click", clickHandler);
      }
      cleanupAutoLoadBenchmark = setupAutoLoadBenchmark();
    },
    onUnmount() {
      cleanupAutoLoadBenchmark();
      if (playButton) {
        playButton.removeEventListener("click", clickHandler);
        thumbnail.removeEventListener("click", clickHandler);
      }
    },
  });
};

export default VideoFacade;
